import React from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { pickGradient } from "../utils/pickGradient";
import { SUPERLATIVE_COPY } from "../utils/superlativeCopy";
import { BrandLockup } from "components";

// Brand colors
const TEAL = "#2ec4b6";
const TEAL_DEEP = "#1a7a72";
const GOLD = "#ffe066";
const CREAM = "#faf2d2";
const INK = "#1c1c1c";

// 4:5 portrait — IG-native (1080x1350). At render size we use 432x540 so it
// previews comfortably on phones. Export uses pixelRatio 2.5 to hit 1080w.
const CARD_W = 432;
const CARD_H = 540;

const VARIANT_GRADIENTS = {
  rank:        `linear-gradient(135deg, ${GOLD} 0%, ${TEAL} 100%)`,
  superlative: `linear-gradient(135deg, #b3a3ff 0%, ${TEAL} 100%)`,
  recap:       `linear-gradient(160deg, ${CREAM} 0%, #cdebe7 100%)`,
  stats:       `linear-gradient(160deg, #ffd9a8 0%, #ffe066 100%)`,
  finalClue:   "linear-gradient(135deg, #2b3b8f 0%, #1a7a72 100%)",
  finalAnswer: "linear-gradient(135deg, #ffe066 0%, #2ec4b6 100%)",
};

const Root = styled(Box, { shouldForwardProp: (p) => p !== "variant" && p !== "header" })(({ variant, header }) => ({
  width: CARD_W,
  height: CARD_H,
  flex: `0 0 ${CARD_W}px`,
  position: "relative",
  borderRadius: 24,
  overflow: "hidden",
  boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
  background: variant === "superlative"
    ? pickGradient(header || "")
    : (VARIANT_GRADIENTS[variant] || VARIANT_GRADIENTS.superlative),
  color: INK,
  fontFamily: "Helvetica Inserat, system-ui",
  display: "flex",
  flexDirection: "column",
  padding: "28px 28px 24px",
}));

// Subtle noise/grain overlay for depth — pure CSS
const Grain = styled(Box)({
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  backgroundImage:
    "radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)",
  backgroundSize: "3px 3px",
  mixBlendMode: "overlay",
  opacity: 0.35,
});

const TopRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "relative",
  zIndex: 1,
});

const GameMeta = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
});

const MetaTitle = styled(Box)({
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 220,
  textAlign: "right",
  fontWeight: 700,
  fontSize: "0.95rem",
  color: INK,
  lineHeight: 1.1,
});

const MetaDate = styled(Box)({
  textAlign: "right",
  fontSize: "0.8rem",
  color: "rgba(28,28,28,0.6)",
  lineHeight: 1.1,
  marginTop: 2,
});

const Body = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  position: "relative",
  zIndex: 1,
});

const PreTag = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "0.9rem",
  letterSpacing: "0.28em",
  textTransform: "uppercase",
  color: "rgba(0,0,0,0.55)",
  fontWeight: 700,
  marginBottom: 8,
});

const Headline = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "3.4rem",
  lineHeight: 0.95,
  color: INK,
  fontWeight: 900,
  letterSpacing: "-0.01em",
});

const SubHeadline = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "1.6rem",
  lineHeight: 1.1,
  color: INK,
  fontWeight: 700,
  marginTop: 12,
});

const SupportLine = styled(Typography)({
  fontSize: "0.95rem",
  color: "rgba(0,0,0,0.7)",
  marginTop: 6,
});

const Sticker = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  borderRadius: 999,
  background: "rgba(255,255,255,0.5)",
  backdropFilter: "blur(8px)",
  fontFamily: "Helvetica Inserat",
  fontSize: "0.85rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  color: INK,
  marginRight: 8,
  marginBottom: 8,
});

const BottomRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "relative",
  zIndex: 1,
  paddingTop: 14,
  borderTop: "1.5px solid rgba(0,0,0,0.15)",
});

const URL = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "0.95rem",
  fontWeight: 800,
  color: INK,
  letterSpacing: "0.04em",
});

const Handle = styled(Typography)({
  fontSize: "0.8rem",
  color: "rgba(0,0,0,0.6)",
  letterSpacing: "0.08em",
});

const StandingsList = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  marginTop: 14,
});

const StandingsRow = styled(Box, { shouldForwardProp: (p) => p !== "isMe" })(({ isMe }) => ({
  display: "grid",
  gridTemplateColumns: "24px 1fr auto",
  alignItems: "center",
  gap: 8,
  padding: "6px 10px",
  borderRadius: 8,
  background: isMe ? "rgba(255,255,255,0.55)" : "transparent",
  fontWeight: isMe ? 800 : 500,
  fontSize: "1rem",
}));

const RecapRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 8,
  padding: "5px 0",
  fontSize: "0.95rem",
  borderBottom: "1px dashed rgba(0,0,0,0.15)",
});

const StatList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  marginTop: 20,
});

const StatRowEl = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: 8,
  borderBottom: "1px dashed rgba(28,28,28,0.25)",
  paddingBottom: 8,
  fontSize: "1.1rem",
});

const StatLabel = styled("span")({ color: INK, fontWeight: 600 });
const StatValue = styled("span")({ color: INK, fontWeight: 800 });

const StatRow = ({ label, value }) => (
  <StatRowEl>
    <StatLabel>{label}</StatLabel>
    <StatValue>{value}</StatValue>
  </StatRowEl>
);

const FinalCategoryLine = styled("div")({
  color: GOLD,
  fontSize: "0.95rem",
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginTop: 16,
});

const FinalClueBlock = styled("div")({
  color: "#fff",
  fontWeight: 700,
  fontSize: "1.35rem",
  lineHeight: 1.25,
  marginTop: 14,
});

const FinalHookLine = styled("div")({
  color: "rgba(255,255,255,0.85)",
  marginTop: 18,
  fontSize: "1rem",
});

const formatDate = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date();
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const Frame = ({ variant, header, gameTitle, gameDate, dark, children }) => {
  const sxLight = dark ? { color: "#fff" } : {};
  const sxMuted = dark ? { color: "rgba(255,255,255,0.7)" } : {};
  const bottomBorderSx = dark
    ? { borderTop: "1.5px solid rgba(255,255,255,0.25)" }
    : {};
  return (
    <Root variant={variant} header={header} className="share-card">
      <Grain />
      <TopRow>
        <BrandLockup
          size="sm"
          variant={dark ? "dark" : "light"}
          shadow={false}
        />
        <GameMeta>
          {gameTitle && <MetaTitle sx={sxLight}>{gameTitle}</MetaTitle>}
          {gameDate && <MetaDate sx={sxMuted}>{formatDate(gameDate)}</MetaDate>}
        </GameMeta>
      </TopRow>
      {children}
      <BottomRow sx={bottomBorderSx}>
        <URL sx={sxLight}>groupardy.com</URL>
        <Handle sx={sxMuted}>@groupardy</Handle>
      </BottomRow>
    </Root>
  );
};

/**
 * variant: "rank" | "superlative" | "recap" | "stats"
 */
const ShareCard = React.forwardRef(({ variant, data, gameTitle, gameDate }, ref) => {
  return (
    <Box ref={ref}>
      {variant === "superlative" && (() => {
        const subhead = SUPERLATIVE_COPY[data.header] ?? data.subhead ?? "";
        return (
          <Frame variant="superlative" header={data.header} gameTitle={gameTitle} gameDate={gameDate}>
            <Body>
              <PreTag>I'm the</PreTag>
              <Headline>{(data.header || "Big Guesser").toUpperCase()}</Headline>
              <SupportLine>{subhead}</SupportLine>
              <Box sx={{ marginTop: 2 }}>
                {data.emoji && <Sticker>{data.emoji} {data.winnerDetail || "earned"}</Sticker>}
                {!data.emoji && data.winnerDetail && <Sticker>⚡ {data.winnerDetail}</Sticker>}
              </Box>
            </Body>
          </Frame>
        );
      })()}

      {variant === "rank" && (
        <Frame variant="rank" gameTitle={gameTitle} gameDate={gameDate}>
          <Body>
            <PreTag>I came in</PreTag>
            <Headline>{(data.rankLabel || "1st").toUpperCase()}</Headline>
            <SubHeadline>{data.playerName}</SubHeadline>
            <SupportLine>${data.score} · {data.correctCount} correct</SupportLine>
            <StandingsList>
              {data.standings?.slice(0, 4).map((row) => (
                <StandingsRow key={row.id} isMe={row.isMe}>
                  <span style={{ color: "rgba(0,0,0,0.55)", fontWeight: 800 }}>{row.rank}</span>
                  <span style={{ fontFamily: "Helvetica Inserat" }}>{row.name}</span>
                  <span style={{ fontFamily: "Helvetica Inserat", color: TEAL_DEEP, fontWeight: 800 }}>${row.score}</span>
                </StandingsRow>
              ))}
            </StandingsList>
          </Body>
        </Frame>
      )}

      {variant === "recap" && (
        <Frame variant="recap" gameTitle={gameTitle} gameDate={gameDate}>
          <Body>
            <PreTag>The Highlights</PreTag>
            <Headline style={{ fontSize: "2.4rem" }}>{gameTitle || ""}</Headline>
            <Box sx={{ marginTop: 2 }}>
              {data.highlights?.slice(0, 5).map((row) => (
                <RecapRow key={row.id}>
                  <span style={{ color: "rgba(0,0,0,0.7)" }}>{row.header}</span>
                  <span style={{ fontFamily: "Helvetica Inserat", color: TEAL_DEEP, fontWeight: 800 }}>{row.winner}</span>
                </RecapRow>
              ))}
            </Box>
            <SubHeadline style={{ marginTop: 16 }}>🏆 {data.winnerName} · ${data.winnerScore}</SubHeadline>
          </Body>
        </Frame>
      )}

      {variant === "stats" && (
        <Frame variant="stats" gameTitle={gameTitle} gameDate={gameDate}>
          <Body>
            <PreTag>My night</PreTag>
            <Headline>{data.playerName}</Headline>
            <SubHeadline sx={{ color: TEAL_DEEP }}>
              ${(data.score ?? 0).toLocaleString("en-US")}
            </SubHeadline>
            <StatList>
              <StatRow label="Correct" value={data.correctCount} />
              <StatRow label="Fastest correct" value={data.fastestText || "—"} />
              {data.accuracyText !== null && (
                <StatRow label="Accuracy" value={data.accuracyText} />
              )}
              <StatRow label="First buzzes" value={data.firstBuzzes} />
              {data.bestCategory && (
                <StatRow
                  label={
                    data.bestCategory.state === "swept" ? "Swept"
                    : data.bestCategory.state === "multipleSweeps" ? "Perfect categories"
                    : "Best category"
                  }
                  value={
                    data.bestCategory.state === "multipleSweeps"
                      ? data.bestCategory.sweepCount
                      : data.bestCategory.categoryName
                  }
                />
              )}
            </StatList>
          </Body>
        </Frame>
      )}

      {variant === "finalClue" && (
        <Frame variant="finalClue" dark gameTitle={gameTitle} gameDate={gameDate}>
          <Body>
            <PreTag sx={{ color: "rgba(255,255,255,0.7)" }}>Final clue</PreTag>
            <FinalCategoryLine>{data.category}</FinalCategoryLine>
            <FinalClueBlock>{data.clueText}</FinalClueBlock>
            <FinalHookLine>
              I nailed Final Groupardy. Can you?
            </FinalHookLine>
          </Body>
        </Frame>
      )}

      {variant === "finalAnswer" && (
        <Frame variant="finalAnswer" gameTitle={gameTitle} gameDate={gameDate}>
          <Body>
            <PreTag>The answer</PreTag>
            <Headline sx={{ fontSize: "2.4rem", lineHeight: 1.1 }}>
              {data.answer}
            </Headline>
            <SupportLine>
              Wagered ${(data.wager ?? 0).toLocaleString("en-US")}.
            </SupportLine>
            <Sticker>✅ I got it.</Sticker>
          </Body>
        </Frame>
      )}
    </Box>
  );
});

export default ShareCard;
